import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AssimagraMetrics, CompanyWithFootprint } from "../../types/assimagra/assimagra-metrics.types";
import { useLanguage } from "../../contexts/LanguageContext";
import { formatNumberDynamic } from "../../utils/numberFormatter";

interface FootprintComparisonChartsProps {
  metrics: AssimagraMetrics | null;
  companies: CompanyWithFootprint[];
}

export const FootprintComparisonCharts = ({ metrics, companies }: FootprintComparisonChartsProps) => {
  const { t } = useLanguage();

  // Use companies data instead of metrics data for more current information
  const chartData = companies.map((company) => ({
    company_id: company.id,
    company_name: company.name,
    associate_number: company.associate_number,
    latest_carbon_footprint: company.latest_carbon_footprint,
    latest_hydric_footprint: company.latest_hydric_footprint,
    activity_type: company.activity_type,
    functional_unit: `${company.total_products_count} produtos`, // Show product count instead
    // Truncate long company names for better display
    display_name: company.associate_number
      ? `#${company.associate_number} ${
          company.name.length > 15 ? company.name.substring(0, 12) + "..." : company.name
        }`
      : company.name.length > 20
      ? company.name.substring(0, 17) + "..."
      : company.name,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Find the company by matching the exact display_name
      const company = chartData.find((c) => c.display_name === label);

      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-2">{company?.company_name || label}</p>
          <p className="text-xs text-gray-500 mb-2">
            {company?.functional_unit} |{" "}
            {company?.activity_type
              ? t(`assimagraDashboard.companiesTable.activityTypes.${company.activity_type}`)
              : ""}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.dataKey === "latest_carbon_footprint"
                ? t("assimagraDashboard.charts.footprintComparison.tooltip.carbonFootprint")
                : t("assimagraDashboard.charts.footprintComparison.tooltip.hydricFootprint")}
              : {formatNumberDynamic(entry.value)}
              {entry.dataKey === "latest_carbon_footprint"
                ? ` ${t("assimagraDashboard.companiesTable.units.carbon")}`
                : ` ${t("assimagraDashboard.companiesTable.units.hydric")}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">
            {t("assimagraDashboard.charts.footprintComparison.title")}
          </CardTitle>
          <p className="text-sm text-gray-500">
            {t("assimagraDashboard.charts.footprintComparison.subtitle", {
              count: 0,
            })}
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-80 text-gray-500">
            <div className="text-center">
              <p className="text-lg mb-2">{t("assimagraDashboard.charts.footprintComparison.noData")}</p>
              <p className="text-sm">{t("assimagraDashboard.charts.footprintComparison.description")}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-gray-900">
          {t("assimagraDashboard.charts.footprintComparison.title")}
        </CardTitle>
        <p className="text-sm text-gray-500">
          {t("assimagraDashboard.charts.footprintComparison.subtitle", {
            count: chartData.length,
          })}
        </p>
      </CardHeader>
      <CardContent>
        <div className="w-full h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 80,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="display_name" angle={-45} textAnchor="end" height={100} interval={0} fontSize={11} />
              <YAxis fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar
                dataKey="latest_carbon_footprint"
                fill="#EF4444"
                name={t("assimagraDashboard.charts.footprintComparison.carbonFootprint")}
                radius={[2, 2, 0, 0]}
              />
              <Bar
                dataKey="latest_hydric_footprint"
                fill="#3B82F6"
                name={t("assimagraDashboard.charts.footprintComparison.hydricFootprint")}
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
